/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { Span } from "@opentelemetry/api";

// ============================================================================
/**
 * can be used to convert an exception object to a meaningful string including
 * stack if possible.
 * @param error
 */
export function verboseFormatException(error: unknown): string {
	return (error instanceof Error)
		? error.stack ?? `${formatException(error)} [missing stacktrace]`
		: (typeof(error) === "string")
			? error
			: `Failed to format exception: ${error}`;
}

// ----------------------------------------------------------------------------
/**
 * Format an error object as string.
 * @param error
 */
export function formatException(error: unknown): string {
	return (error instanceof Error)
		? `${error.name}: ${error.message}`
		: (typeof(error) === "string")
			? error
			: `Failed to format exception: ${error}`;
}

// ----------------------------------------------------------------------------
/**
 * Stringify all own properties of an object into a string.
 * @param obj to object to serialize
 */
export function prettyPrintObject<T extends Record<string, unknown>>(obj: T): string {
	return Object.entries(obj)
		.filter(([, v]) => v != null)
		.map(([k, v]) => `${k}=${v}`)
		.join(", ");
}

// ----------------------------------------------------------------------------
/**
 * Stringify a span into "<traceId>:<spanId>:<isRecording>" or "-"" if no span is given
 * @param span the span
 */
export function spanToString(span: Span | null | undefined): string {
	if (span != null) {
		const ctx = span.spanContext();
		return `${ctx.traceId}:${ctx.spanId}:${span.isRecording()}`;
	}
	return "-";
}
